import { Component, OnInit, OnDestroy, Directive, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { Router } from '@angular/router';

import { AppConstants } from 'appshared/app-constants';
import * as Models from 'appshared/shared-models';
import { TextTools } from 'appshared/text-tools';
import { MiscTools } from 'appshared/misc-tools';
import { ValidationTools } from 'appshared/validation-tools';

import { AuthService } from 'client/app/services/auth.service';
import { UiAlertsService } from 'client/app/components/ui-alerts/ui-alerts.service';

import { PagingSearchBarComponent } from 'client/app/components/shared/paging-search-bar/paging-search-bar.component';

@Component({
	selector: 'app-table-users',
	templateUrl: './table-users.component.html',
	styleUrls: ['./table-users.component.scss']
})
export class TableUsersComponent implements OnInit {
	ac = AppConstants;
	tt = TextTools;
	mt = MiscTools;
	now = new Date();

	@ViewChild(PagingSearchBarComponent) pagingSearchBar: PagingSearchBarComponent = null;

	/*
		pass in
			users 
			storageKey
			options
			- showOrganization : true
	*/

	authUser: Models.AuthUser = null;
	loading: boolean = false;

	displayOptions: Models.TableDisplayOptions = new Models.TableDisplayOptions();
	pagingOptions: Models.TablePagingOptions = new Models.TablePagingOptions();
	storageKey: string = '';

	showOrganization: boolean = true;
	linkUser: boolean = false;

	items: Models.User[] = [];
	itemsToShow: Models.User[] = [];

	columnDefs: Models.ColumnDefinition[] = [
		{
			field: 'is_enabled',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Enabled/Disabled',
			icon: 'power-off'
		}, {
			field: '__onboard_status',
			type: 'baseText',
			sortType: 'text',
			label: null,
			toolTip: 'On-boarding Status',
			icon: 'exclamation-triangle'
		}, {
			field: 'name',
			type: 'baseText',
			sortType: 'text',
			label: 'Name',
			toolTip: null,
			icon: null
		}, {
			field: 'org_name',
			type: 'baseText',
			sortType: 'text',
			label: 'Organization',
			toolTip: null,
			icon: null
		}, {
			field: 'email',
			type: 'baseText',
			sortType: 'text',
			label: 'E-Mail',
			toolTip: null,
			icon: null
		}, {
			field: 'role',
			type: 'baseText',
			sortType: 'text',
			label: null,
			toolTip: 'Role',
			icon: 'user'
		}, {
			field: '__notifications',
			type: 'baseText',
			sortType: 'text',
			label: null,
			toolTip: 'Notifications',
			icon: AppConstants.sectionIcons.mynotifications
		}, {
			field: '__num_packages',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Number of Packages',
			icon: AppConstants.sectionIcons.mypackages
		}, {
			field: '__num_keys',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Number of Keys',
			icon: AppConstants.sectionIcons.mykeys
		}, {
			field: '__num_groups',
			type: 'baseNumeric',
			sortType: 'numeric',
			label: null,
			toolTip: 'Number of Groups',
			icon: AppConstants.sectionIcons.usergroups
		}, {
			field: 'added_on',
			type: 'baseDate',
			sortType: 'numeric',
			label: null,
			toolTip: 'Days Since Added',
			icon: 'clock'
		}, {
			field: 'last_login',
			type: 'baseDate',
			sortType: 'numeric',
			label: null,
			toolTip: 'Days Since Last Login',
			icon: 'person-to-portal'
		}, {
			field: '__flag',
			type: 'baseText',
			sortType: 'text',
			label: null,
			toolTip: 'Last Login Country',
			icon: 'flag'
		}, {
			field: 'last_download',
			type: 'baseDate',
			sortType: 'numeric',
			label: null,
			toolTip: 'Days Since Last Download',
			icon: 'file-download'
		}
	];

	fieldsToShow: string[] = [];
	allFieldsToShow: string[] = [];
	lockedFields: string[] = [];
	baselineFields: string[] = [];
	minimalFields: string[] = [];


	// ------------------------------------------------------------------------
	constructor(
		private router: Router,
		private uiAlertsService: UiAlertsService,
		private authService: AuthService) {
	}

	// ------------------------------------------------------------------------
	ngOnInit(): void {
		const sessionUser: Models.AuthUser = this.authService.getUser();
		if (sessionUser != null && sessionUser.id !== 0) {
			this.authUser = sessionUser;
			this.setups();
		} // if
	}

	// ------------------------------------------------------------------------
	async setups() {

	}

	// ------------------------------------------------------------------------
	updateContent(items: Models.User[], storageKey: string, options: any = {}) {
		this.loading = true;

		this.items = items;
		this.storageKey = storageKey;

		let optionKeys: string[] = [];
		if (options) optionKeys = Object.keys(options);

		if (optionKeys.includes('showOrganization')) this.showOrganization = options.showOrganization;

		this.fieldsToShow = [];
		this.allFieldsToShow = [];
		this.lockedFields = [];
		this.baselineFields = [];
		this.minimalFields = [];

		this.allFieldsToShow.push('is_enabled');
		this.allFieldsToShow.push('__onboard_status');
		this.allFieldsToShow.push('name');
		this.lockedFields.push('name');
		if (this.showOrganization) this.allFieldsToShow.push('org_name');
		this.allFieldsToShow.push('email');
		this.allFieldsToShow.push('role');
		this.allFieldsToShow.push('__notifications');
		this.allFieldsToShow.push('__num_packages');
		this.allFieldsToShow.push('__num_keys');
		this.allFieldsToShow.push('__num_groups');
		this.allFieldsToShow.push('added_on');
		this.allFieldsToShow.push('last_login');
		this.allFieldsToShow.push('__flag');
		this.allFieldsToShow.push('last_download');

		this.baselineFields = MiscTools.deepClone(this.allFieldsToShow);

		for (const item of this.items) {
			if (item.is_onboarded === 0)
				item['__onboard_status'] = ValidationTools.getUserIssues(item, true).join(' ');
			else
				item['__onboard_status'] = 'On-boarded';

			if (item.notifications)
				item['__notifications'] = 'User has setup notifications';
			else
				item['__notifications'] = 'User has not setup notifications';

			item['__num_packages'] = item.package_ids.length;
			item['__num_keys'] = item.num_keys;
			item['__num_groups'] = item.group_ids.length;

			const location: string = item.last_login_location ? item.last_login_location : '';
			const parts: string[] = TextTools.trimList(location.split(','));
			if (parts.length > 1) {
				const country: string = parts[parts.length - 1];
				const prov: string = parts[parts.length - 1];
				item['__flag'] = AppConstants.flagBase + '/' + country.toLowerCase() + '.svg';
			} // if
		} // for

		this.pagingSearchBar.setupPaging(this.items, this.columnDefs, this.storageKey, 'name', 'asc', false, this.allFieldsToShow, this.lockedFields, this.baselineFields, this.minimalFields);


		this.loading = false;
	} // updateContent

	// ------------------------------------------------------------------------
	filterItems(matchWords: string[]): any[] {

		const tmpList: Models.User[] = [];
		for (const item of this.items) {
			let match: boolean = true;
			if (matchWords.length > 0) {
				let matches: number = 0;
				for (const w of matchWords) {
					if (item.name.toLowerCase().includes(w)
						|| item.email.toLowerCase().includes(w)
						|| item.role.toLowerCase().includes(w)
						|| (this.showOrganization && item.org_name.toLowerCase().includes(w))
						|| (item.last_login_location && item.last_login_location.toLowerCase().includes(w))
					)
						matches++;
				} // for
				if (matches !== matchWords.length) match = false;
			} // if

			if (match) tmpList.push(item);
		} // for

		return tmpList;
	} // filterItems

	// ------------------------------------------------------------------------
	setSortBy(field: string) {
		this.pagingSearchBar.setSortBy(field);
	} // setSortBy

	// ------------------------------------------------------------------------
	// Catch calls from the paging/search bar
	// ------------------------------------------------------------------------
	getParentMethod(): any {
		return {
			filterItems: (matchWords: string[]) => {
				return this.filterItems(matchWords);
			},
			updateItemsToShow: (itemsToShow: any[], displayOptions: Models.TableDisplayOptions, pagingOptions: Models.TablePagingOptions) => {
				this.itemsToShow = itemsToShow;
				this.displayOptions = displayOptions;
				this.pagingOptions = pagingOptions;
			},
			updateFieldsToShow: (fieldsToShow: string[]) => {
				this.fieldsToShow = fieldsToShow;
			},
			setPageNum: (pageNum: number, uniqueId: string) => {
				if (this.pagingSearchBar) this.pagingSearchBar.setPageNum(pageNum);
			},
			setSortBy: (field: string) => {
				this.setSortBy(field);
			}
		}
	}

}
